import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Calendar } from "../../../../ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../ui/select";
import { supabaseReservation } from "../../../../../integrations/supabase/client";
import { useToast } from "../../../../../hooks/use-toast";
import { useAuth } from "../../../../../contexts/AuthContext";
interface Reservation {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  date: string;
  time: string;
  guests: number;
  status: string;
  outlet_id?: string | null;
}

interface Outlet {
  id: string;
  name: string;
  is_active: boolean;
}

interface TimeSlot {
  id: string;
  day_type: string;
  start_time: string;
  end_time: string;
  slot_duration: number;
  is_active: boolean;
  outlet_id: string | null;
}

interface ReservationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: Reservation | null;
  selectedDate: Date | undefined;
  onSuccess: () => void;
}

export function ReservationDialog({
  open,
  onOpenChange,
  reservation,
  selectedDate,
  onSuccess,
}: ReservationDialogProps) {
  const { toast } = useToast();
  const { accountId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [date, setDate] = useState<Date | undefined>(selectedDate);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    time: "",
    guests: 2,
    outlet_id: "",
  });
  useEffect(() => {
    if (reservation) {
      // Normalize time format (remove seconds if present)
      const normalizedTime = reservation.time ? reservation.time.slice(0, 5) : "";
      setFormData({
        name: reservation.name,
        email: reservation.email,
        phone: reservation.phone || "",
        time: normalizedTime,
        guests: reservation.guests,
        outlet_id: reservation.outlet_id || "",
      });
      setDate(new Date(reservation.date));
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
        time: "",
        guests: 2,
        outlet_id: "",
      });
      setDate(selectedDate || new Date());
    }
  }, [reservation, open, selectedDate]);

  useEffect(() => {
    fetchTimeSlots();
    fetchOutlets();
  }, []);

  const fetchOutlets = async () => {
    const { data } = await supabaseReservation
      .from("outlets")
      .select("id, name, is_active")
      .eq("is_active", true)
      .order("name");
    if (data) setOutlets(data);
  };

  const fetchTimeSlots = async () => {
    const { data } = await supabaseReservation
      .from("time_slots")
      .select("*")
      .eq("is_active", true);
    if (data) setTimeSlots(data);
  };

  const getAvailableTimeSlots = () => {
    if (!date) return [];
    
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const dayType = isWeekend ? "weekend" : "weekday";
    
    // Filter time slots by day type and selected outlet
    const matchingSlots = timeSlots.filter((s) => {
      const dayMatches = s.day_type === dayType;
      // If an outlet is selected, match that outlet's slots or slots with no outlet (global)
      // If no outlet selected, use slots with no outlet (global) or any available
      if (formData.outlet_id) {
        return dayMatches && (s.outlet_id === formData.outlet_id || s.outlet_id === null);
      }
      return dayMatches;
    });

    if (matchingSlots.length === 0) return [];

    // Combine all matching time slots into one list
    const allSlots: string[] = [];
    
    matchingSlots.forEach((slot) => {
      const [startHour, startMin] = slot.start_time.split(":").map(Number);
      const [endHour, endMin] = slot.end_time.split(":").map(Number);
      
      let currentMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      while (currentMinutes < endMinutes) {
        const hours = Math.floor(currentMinutes / 60);
        const mins = currentMinutes % 60;
        const timeStr = `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
        if (!allSlots.includes(timeStr)) {
          allSlots.push(timeStr);
        }
        currentMinutes += slot.slot_duration;
      }
    });

    // Sort the time slots
    return allSlots.sort();
  };

  const formatTimeDisplay = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const fetchRestaurantName = async () => {
    const { data } = await supabaseReservation
      .from("agent_config")
      .select("restaurant_name")
      .limit(1)
      .maybeSingle();
    return data?.restaurant_name || "Restaurant";
  };

  const sendEmail = async (type: "confirmation" | "update" | "cancellation") => {
    const restaurantName = await fetchRestaurantName();
    
    await supabaseReservation.functions.invoke("send-reservation-confirmation", {
      body: {
        name: formData.name,
        email: formData.email,
        date: date ? format(date, "EEEE, MMMM d, yyyy") : reservation?.date,
        time: formatTimeDisplay(formData.time || reservation?.time || ""),
        guests: formData.guests,
        restaurantName,
        emailType: type,
      },
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.time || !date) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);

    try {
      if (reservation) {
        // Update existing reservation
        const { error } = await supabaseReservation
          .from("reservations")
          .update({
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            date: format(date, "yyyy-MM-dd"),
            time: formData.time,
            guests: formData.guests,
            outlet_id: formData.outlet_id || null,
          })
          .eq("id", reservation.id);
        if (error) throw error;

        await sendEmail("update");
        toast({
          title: "Reservation Updated",
          description: "The reservation has been updated and the client notified",
        });
      } else {
        // Create new reservation
        const { error } = await supabaseReservation.from("reservations").insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          date: format(date, "yyyy-MM-dd"),
          time: formData.time,
          guests: formData.guests,
          outlet_id: formData.outlet_id || null,
          status: "confirmed",
          account_id: accountId,
        });
        if (error) throw error;

        await sendEmail("confirmation");
        toast({
          title: "Reservation Created",
          description: "The reservation has been created and confirmation sent",
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving reservation:", error);
      
      // Check if it's a blocked date error
      const errorMessage = error?.message || error?.details || "Failed to save reservation";
      const isBlockedDate = errorMessage.toLowerCase().includes("blocked") || 
                           errorMessage.toLowerCase().includes("unavailable");
      
      toast({
        title: isBlockedDate ? "Date/Time Unavailable" : "Error",
        description: isBlockedDate 
          ? "This date or time slot is blocked and not available for reservations."
          : errorMessage,
        variant: "destructive",
      });
    }

    setLoading(false);
  };

  const availableSlots = getAvailableTimeSlots();
  
  // When editing, ensure the current time is included in options even if not in available slots
  const timeOptions = (() => {
    if (reservation && formData.time && !availableSlots.includes(formData.time)) {
      return [formData.time, ...availableSlots];
    }
    return availableSlots;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {reservation ? "Edit Reservation" : "New Reservation"}
          </DialogTitle>
          <DialogDescription>
            Fill in the reservation details below
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Guest name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="guest@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Phone number"
            />
          </div>

          <div className="space-y-2">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => {
                    setDate(newDate);
                    setFormData({ ...formData, time: "" });
                  }}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {outlets.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="outlet">Outlet</Label>
              <Select
                value={formData.outlet_id}
                onValueChange={(value) => setFormData({ ...formData, outlet_id: value, time: "" })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="time">Time *</Label>
              <Select
                value={formData.time}
                onValueChange={(value) => setFormData({ ...formData, time: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.length > 0 ? (
                    timeOptions.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {formatTimeDisplay(slot)}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-slots" disabled>
                      No slots available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guests">Guests</Label>
              <Select
                value={formData.guests.toString()}
                onValueChange={(value) =>
                  setFormData({ ...formData, guests: parseInt(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 25 }, (_, i) => i + 1).map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} {num === 1 ? "guest" : "guests"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {reservation ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
